import * as _ from 'lodash';
import { safeHref } from '../helper';

// Search UI stores each field as {raw: value}; _index lives on the raw hit meta.
export function columnValue(result, field) {
  if (field === '_index') {
    return _.get(result, '_meta.rawHit._index', '');
  }
  return _.get(result, `${field}.raw`, _.get(result, field, ''));
}

// Rows for one configured group: those whose groupBy value matches the group's `match`.
// With no groupBy, every result belongs to the single table.
export function groupRows(results, groupBy, match) {
  if (!groupBy) return results;
  return results.filter(r => `${columnValue(r, groupBy)}`.trim() === `${match ?? ''}`.trim());
}

// Empty values sort last regardless of direction (so the `* factor` below never flips
// them). Returns the comparator result for the empty cases, or null when both values are
// present so the caller runs the actual comparison.
function emptyLast(aEmpty, bEmpty) {
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  return null;
}

// Sort a copy of rows by one column. Numeric columns compare as numbers, others as
// case-insensitive strings; empty/non-numeric values sort last regardless of direction.
// Returns rows unchanged when no field is selected.
// Sorts the current page's rows client-side. For full-dataset sorting,
// an Elasticsearch sort clause would be needed instead.
export function sortRows(rows, {field, dir = 'asc', numeric = false} = {}) {
  if (!field) return rows;
  const factor = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = columnValue(a, field);
    const bv = columnValue(b, field);
    if (numeric) {
      const an = parseFloat(av);
      const bn = parseFloat(bv);
      const ranked = emptyLast(Number.isNaN(an), Number.isNaN(bn));
      if (ranked !== null) return ranked;
      return (an - bn) * factor;
    }
    const ranked = emptyLast(
      av === '' || av === null || av === undefined,
      bv === '' || bv === null || bv === undefined
    );
    if (ranked !== null) return ranked;
    return `${av}`.localeCompare(`${bv}`, 'en', {sensitivity: 'base'}) * factor;
  });
}

// Allowlisted <a> attributes — admin-authored, so never pass through event handlers or href.
const ALLOWED_LINK_ATTRS = ['target', 'rel', 'class', 'title'];
const DEFAULT_LINK_ATTRS = {target: '_blank', rel: 'nofollow noopener'};

export function linkAttributes(raw) {
  let parsed = {};
  if (raw && `${raw}`.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
  }

  const safe = {...DEFAULT_LINK_ATTRS};
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return safe;
  }
  for (const [key, value] of Object.entries(parsed)) {
    if (ALLOWED_LINK_ATTRS.includes(key) || key.startsWith('data-')) {
      safe[key] = value;
    }
  }
  // Enforce noopener on new-tab links so a custom `rel` can't reintroduce
  // reverse-tabnabbing (the opened page gaining window.opener access).
  if (`${safe.target}` === '_blank') {
    const rel = new Set(`${safe.rel || ''}`.split(/\s+/).filter(Boolean));
    rel.add('noopener');
    safe.rel = Array.from(rel).join(' ');
  }
  return safe;
}

export { safeHref };
